import { SetFieldType } from 'type-fest';

import { isUndefined } from '../type-guards';
import {
  UnitSymbol,
  getLengthUnitSymbol,
  getSpeedUnitSymbol,
  getWeightUnitSymbol,
} from './unit-symbol';
import { UnitSystem } from './unit-system';
import { UnitType } from './unit-type';

const FACTOR_KG_TO_LBS = 2.20462262185;
const FACTOR_MS_TO_KMH = 3.6;
const FACTOR_MS_TO_MPH = 2.23693629205;
const FACTOR_METERS_TO_MILES = 0.000621371192;

/**
 * A function that formats a converted value for display.
 *
 * @param value - The value to format.
 * @returns The formatted value.
 */
export type ConversionFormatter = (value: number | string) => string;

/**
 * Formatter to display the intensity of a value.
 */
export const IntensityConversionFormatter: ConversionFormatter = (value) => `${value}/min`;

type ConversionOptionsByUnitType = {
  [UnitType.ABSOLUTE]: AbsoluteConversionOptions;
  [UnitType.DURATION]: DurationConversionOptions;
  [UnitType.LENGTH]: LengthConversionOptions;
  [UnitType.PERCENTAGE]: PercentageConversionOptions;
  [UnitType.SPEED]: SpeedConversionOptions;
  [UnitType.WEIGHT]: WeightConversionOptions;
};

export type ConversionOptions<TType extends UnitType> = ConversionOptionsByUnitType[TType];

export interface AbsoluteConversionOptions {
  decimals?: number;
  formatter?: ConversionFormatter;
}

export interface DurationConversionOptions {
  withSeconds: boolean;
  withSymbol: boolean;
}

export interface LengthConversionOptions {
  decimals?: number;
  formatter?: ConversionFormatter;
  from: UnitSystem;
  to: UnitSystem;
  withSymbol: boolean;
}

export interface PercentageConversionOptions {
  decimals?: number;
  withSymbol: boolean;
}

export interface SpeedConversionOptions {
  decimals?: number;
  to: UnitSystem;
  withSymbol: boolean;
}

export interface WeightConversionOptions {
  decimals?: number;
  from: UnitSystem;
  to: UnitSystem;
  withSymbol: boolean;
}

/**
 * Converts an absolute value by optionally rounding it to the specified number of decimal places.
 *
 * @param value - The value to convert.
 * @param options - The options for the conversion.
 * @returns The rounded absolute value, if specified.
 */
export function convertAbsolute(
  value: number,
  options: SetFieldType<AbsoluteConversionOptions, 'formatter', undefined>,
): number;
export function convertAbsolute(
  value: number,
  options: SetFieldType<AbsoluteConversionOptions, 'formatter', ConversionFormatter>,
): string;
export function convertAbsolute(
  value: number,
  options: ConversionOptions<UnitType.ABSOLUTE>,
): number | string {
  const converted = roundToDecimals(value, options.decimals);

  return options.formatter ? options.formatter(converted) : converted;
}

/**
 * Converts a duration value in seconds to a formatted string representation.
 *
 * @param value The duration value in seconds.
 * @returns The formatted duration string in the format 'HH:mm:ss'.
 */
export function convertDuration(value: number, options: DurationConversionOptions): string {
  // date-fns has no zero-padded clock formatter for durations, so break the seconds down by hand.
  // Hours are the total, not a component, so durations of a day or more keep counting up.
  const hours = Math.floor(value / 3_600);
  const minutes = Math.floor(value / 60) % 60;
  const seconds = Math.floor(value) % 60;

  if (options.withSymbol) {
    return [
      hours + UnitSymbol.HOURS,
      minutes + UnitSymbol.MINUTES,
      ...(options.withSeconds ? [seconds + UnitSymbol.SECONDS] : []),
    ].join(' ');
  }

  const pad = (part: number): string => String(part).padStart(2, '0');

  return [pad(hours), pad(minutes), ...(options.withSeconds ? [pad(seconds)] : [])].join(':');
}

/**
 * Converts a number to a length value.
 *
 * @param value - The number to be converted.
 * @param options - The options for length conversion.
 * @returns The converted length value.
 */
export function convertLength(
  value: number,
  options: SetFieldType<LengthConversionOptions, 'withSymbol', false>,
): number;
export function convertLength(
  value: number,
  options: SetFieldType<LengthConversionOptions, 'withSymbol', true>,
): string;
export function convertLength(value: number, options: LengthConversionOptions): number | string;
export function convertLength(value: number, options: LengthConversionOptions): number | string {
  let converted: number;

  if (options.from === options.to) {
    converted = value;
  } else if (options.to === UnitSystem.METRIC) {
    converted = value / FACTOR_METERS_TO_MILES;
  } else {
    converted = value * FACTOR_METERS_TO_MILES;
  }

  const rounded = roundToDecimals(converted, options.decimals);
  const unitSymbol = getLengthUnitSymbol(options.to);

  const withSymbol = options.withSymbol ? withUnitSymbol(rounded, unitSymbol) : rounded;

  return options.formatter ? options.formatter(withSymbol) : withSymbol;
}

/**
 * Converts a number to a percentage value.
 *
 * @param value - The number to be converted.
 * @param options - The options for percentage conversion.
 * @returns The converted percentage value.
 */
export function convertPercentage(
  value: number,
  options: SetFieldType<PercentageConversionOptions, 'withSymbol', false>,
): number;
export function convertPercentage(
  value: number,
  options: SetFieldType<PercentageConversionOptions, 'withSymbol', true>,
): string;
export function convertPercentage(
  value: number,
  options: PercentageConversionOptions,
): number | string;
export function convertPercentage(
  value: number,
  options: PercentageConversionOptions,
): number | string {
  const percentage = roundToDecimals(value * 100, options.decimals);

  return options.withSymbol ? withUnitSymbol(percentage, UnitSymbol.PERCENTAGE) : percentage;
}

/**
 * Converts a number to a speed value.
 * This method currently only supports conversion from m/s.
 *
 * @param value - The number to be converted.
 * @param options - The options for speed conversion.
 * @returns The converted speed value.
 */
export function convertSpeed(
  value: number,
  options: SetFieldType<SpeedConversionOptions, 'withSymbol', false>,
): number;
export function convertSpeed(
  value: number,
  options: SetFieldType<SpeedConversionOptions, 'withSymbol', true>,
): string;
export function convertSpeed(value: number, options: SpeedConversionOptions): number | string;
export function convertSpeed(value: number, options: SpeedConversionOptions): number | string {
  let converted: number;

  if (options.to === UnitSystem.METRIC) {
    converted = value * FACTOR_MS_TO_KMH;
  } else {
    converted = value * FACTOR_MS_TO_MPH;
  }

  const rounded = roundToDecimals(converted, options.decimals);
  const unitSymbol = getSpeedUnitSymbol(options.to);

  return options.withSymbol ? withUnitSymbol(rounded, unitSymbol) : rounded;
}

/**
 * Converts a number to a weight value.
 *
 * @param value - The number to be converted.
 * @param options - The options for weight conversion.
 * @returns The converted weight value.
 */
export function convertWeight(
  value: number,
  options: SetFieldType<WeightConversionOptions, 'withSymbol', false>,
): number;
export function convertWeight(
  value: number,
  options: SetFieldType<WeightConversionOptions, 'withSymbol', true>,
): string;
export function convertWeight(value: number, options: WeightConversionOptions): number | string;
export function convertWeight(value: number, options: WeightConversionOptions): number | string {
  let converted: number;

  if (options.from === options.to) {
    converted = value;
  } else if (options.to === UnitSystem.METRIC) {
    converted = value / FACTOR_KG_TO_LBS;
  } else {
    converted = value * FACTOR_KG_TO_LBS;
  }

  const rounded = roundToDecimals(converted, options.decimals);
  const unitSymbol = getWeightUnitSymbol(options.to);

  return options.withSymbol ? withUnitSymbol(rounded, unitSymbol) : rounded;
}

/**
 * Rounds a number to the specified number of decimal places.
 * If no decimal places are specified, the original value is returned.
 *
 * @param value - The number to round.
 * @param decimals - The number of decimal places to round to.
 * @returns The rounded number.
 */
function roundToDecimals(value: number, decimals?: number): number {
  if (isUndefined(decimals)) {
    return value;
  }

  const factor = Math.pow(10, decimals);

  return Math.round(value * factor) / factor;
}

/**
 * Attaches a unit symbol to a value.
 *
 * @param value - The value to attach the symbol to.
 * @param symbol - The symbol to attach.
 * @returns The value with the attached symbol.
 */
function withUnitSymbol(value: string | number, symbol: UnitSymbol): string {
  return `${value} ${symbol}`;
}
